import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCarrierAuth } from "src/components/auth/CarrierAuthProvider";
import { useTranslation } from "react-i18next";
import { LoadingBar } from "src/M_Components";

/**
 * Small helper to parse query string from the current location.
 * This works with HashRouter because React Router exposes `location.search`
 * with the part after "?" inside the hash.
 */
function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

/**
 * Storage key that holds a normalized "pending invite" context.
 *
 * This is written by CarrierInviteEntryPage when an invite link is opened,
 * and can be read later by CarrierLoginPage or CarrierHomePage to resume
 * the invite flow after authentication.
 *
 * The expected shape is:
 * {
 *   type: "spot" | "phase",
 *   tenderId: number | null,
 *   tenderPhaseId: number | null,
 *   token: string,
 *   createdAt: number
 * }
 */
export const PENDING_INVITE_KEY = "carrier_invite_pending";

/**
 * Internal route where the actual invite/bid wrapper page
 * (CarrierInviteViewBidPage) is mounted inside the carrier realm.
 *
 * This must be an absolute path (starting with "/") so that React Router
 * navigates correctly under HashRouter.
 *
 * CarrierInviteEntryPage and CarrierLoginPage use this constant to ensure
 * they both redirect to the same place after validating the invite.
 */
export const INVITE_TARGET_ROUTE = "/carrier/home/CarrierInviteViewBid";

/**
 * Entry page for carrier invite links.
 *
 * Responsibilities:
 * - Parse the invite query string (?type=..., ?tenderId=..., ?tenderPhaseId=..., ?token=...).
 * - Validate that the invite is structurally valid.
 * - Persist a normalized "pending invite" context into localStorage so it can
 *   survive redirects (for example, when sending the user to login).
 * - Decide whether to:
 *     * Redirect straight to the internal invite view when the carrier is already authenticated.
 *     * Redirect to the carrier login page when authentication is required.
 *
 * This page is intended to be a short-lived transition screen.
 */
function CarrierInviteEntryPage() {
  const query = useQuery();
  const location = useLocation(); // IMPORTANT: HashRouter-safe access to "?..."
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useCarrierAuth();
  const { t } = useTranslation();

  useEffect(() => {
    // 1) Extract raw query parameters from the URL.
    const typeRaw = query.get("type") || "";
    const type = typeRaw.toLowerCase(); // "spot" | "phase"
    const tenderIdRaw = query.get("tenderId");
    const phaseIdRaw = query.get("tenderPhaseId");
    const token = query.get("token") || null;

    // 2) Basic validation of the invite payload.
    const isSpot = type === "spot";
    const isPhase = type === "phase";

    const tenderId = tenderIdRaw ? Number(tenderIdRaw) : null;
    const tenderPhaseId = phaseIdRaw ? Number(phaseIdRaw) : null;

    const hasValidSpot =
      isSpot && Number.isFinite(tenderId) && !!tenderId && !!token;
    const hasValidPhase =
      isPhase &&
      Number.isFinite(tenderPhaseId) &&
      !!tenderPhaseId &&
      !!token;

    if (!hasValidSpot && !hasValidPhase) {
      // Invalid or incomplete invite -> fallback to the generic carrier entry.
      // The "replace" option avoids leaving a broken invite URL in the history.
      navigate("/carrier-inviting", { replace: true });
      return;
    }

    // 3) Persist a normalized "pending invite" context in localStorage.
    //    This allows:
    //    - CarrierLoginPage to read it after the user authenticates and then
    //      redirect directly into the invite view.
    //    - CarrierHome nested routes to re-use the same context if needed.
    const pendingContext = {
      type: isPhase ? "phase" : "spot",
      tenderId: hasValidSpot ? tenderId : null,
      tenderPhaseId: hasValidPhase ? tenderPhaseId : null,
      token,
      createdAt: Date.now(),
    };

    try {
      window.localStorage.setItem(
        PENDING_INVITE_KEY,
        JSON.stringify(pendingContext)
      );
    } catch {
      // If localStorage is not available or fails, we still continue.
      // In that case the invite flow will rely only on the current query string.
    }

    // 4) If carrier auth is still hydrating from storage, wait until it finishes.
    //    The effect will re-run when "loading" changes.
    if (loading) return;

    // 5) Decide the next step based on the current auth state:
    //    - If already authenticated, go straight to the internal invite view.
    //    - If not authenticated yet, send the user to the carrier login page.
    //
    // IMPORTANT:
    // With HashRouter, the query string lives inside the hash fragment.
    // Therefore we must use React Router's `location.search` (not window.location.search)
    // to reliably preserve "?type=...&token=..." across navigations.
    const search = location.search || "";

    if (isAuthenticated) {
      // IMPORTANT:
      // If the carrier is already authenticated, we can safely remove the pending invite
      // from localStorage to prevent re-triggering the flow when the user later returns
      // to "/carrier/home" (e.g., after closing the invite tab).
      try {
        window.localStorage.removeItem(PENDING_INVITE_KEY);
      } catch { }

      navigate(`${INVITE_TARGET_ROUTE}${search}`, { replace: true });
    } else {
      navigate(`/carrier/login${search}`, { replace: true });
    }
  }, [query, location.search, navigate, isAuthenticated, loading]);

  // IMPORTANT:
  return <LoadingBar />; // We intentionally render a "normal" loader here (LoadingBar) instead of a background
  // "processing" text screen. This avoids the UX of seeing text behind the global
  // gray overlay (MRouter overlay) during early boot/hydration.
  //
  // Result: even if the global overlay is briefly visible, users only see one
  // consistent loading experience.

}

export default CarrierInviteEntryPage;
